// React adapter for the shared METooltips engine. The document engine owns all chip markup,
// styling, events and placement; this bridge registers table hints authored before the Tier B
// attribute contract, so no data-tooltip lands on a production element.
import React, { cloneElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

export const Tooltip = ({ text, disabled = false, children }) => {
  const id = `me-table-description-${useId().replace(/:/g, '')}`
  const ref = useRef(null)
  const inactive = disabled || !text
  useLayoutEffect(() => {
    if (!inactive && ref.current && window.METooltips) {
      return window.METooltips.registerLegacy(ref.current, text)
    }
    return undefined
  }, [inactive, text])
  const child = React.Children.only(children)
  if (inactive) return child
  const trigger = cloneElement(child, {
    ref: node => {
      ref.current = node
      if (typeof child.ref === 'function') child.ref(node)
      else if (child.ref) child.ref.current = node
    },
    'aria-describedby': [child.props['aria-describedby'], id].filter(Boolean).join(' '),
  })
  return (
    <>
      {trigger}
      {typeof document !== 'undefined' &&
        createPortal(
          <span id={id} hidden>
            {text}
          </span>,
          document.body
        )}
    </>
  )
}

Tooltip.propTypes = {
  text: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.element.isRequired,
}

/**
 * Text that discloses its full value only while it is visually clipped. Unclipped, it is plain
 * text and adds no tab stop.
 */
export const ClippedValue = ({ text, className }) => {
  const ref = useRef(null)
  const [clipped, setClipped] = useState(false)

  const measure = useCallback(() => {
    const node = ref.current
    if (!node) return
    setClipped(node.scrollWidth > node.clientWidth)
  }, [])

  useLayoutEffect(() => {
    measure()
  }, [text, measure])

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    window.addEventListener('resize', measure)
    let observer = null
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(measure)
      observer.observe(node)
      if (node.parentElement) observer.observe(node.parentElement)
    }
    return () => {
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [measure])

  return (
    <Tooltip text={text} disabled={!clipped}>
      <span
        ref={ref}
        className={['me-table-clipped', className].filter(Boolean).join(' ')}
        tabIndex={clipped ? 0 : undefined}
      >
        {text}
      </span>
    </Tooltip>
  )
}

ClippedValue.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
}
